/**
 * RISK-EXPORT.MODAL
 * Export risks (Excel, PDF, Share link)
 */

import Modal from "@/components/modal"
import CardContainer from "@/components/card-container"
import CardButton from "@/components/card-button"
import { faFileExcel, faFilePdf, faLink } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { STATUS_LOADED, STATUS_SAVING } from "@/redux/_status.types"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { t } from "i18next"
import { exportRisksToXlsx } from "@/features/risks/entities/risk/utils/exportRisksToXlsx"
import { exportRisksToPdf } from "@/features/risks/entities/risk/utils/exportRisksToPdf"
import { Session } from "@/redux/_session.types"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import establishmentStore from "@/features/adm/entities/establishment/model/establishment.store"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import { toast } from "react-toastify"

interface OwnProps {
  onClose: Function
}

const CARDS = [
  {
    id: "excel",
    icon: faFileExcel,
    title: "Exporter en Excel",
    text: "Téléchargez votre document unique au format Excel."
  },
  {
    id: "pdf",
    icon: faFilePdf,
    title: "Exporter en PDF",
    text: "Téléchargez un PDF de présentation à communiquer aux salariés."
  },
  {
    id: "link",
    icon: faLink,
    title: "Partager un lien",
    text: "Partagez votre document unique au format numérique, en consultation à la date du jour."
  }
]

export default function RiskExportModal({ onClose }: OwnProps) {
  const dispatch = useAppDispatch()

  const _session: Session = useAppSelector((state) => state._session)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const organization = useAppSelector(organizationStore.selectors.active)
  const risks = useAppSelector(riskStore.selectors.list)
  const units = useAppSelector(unitStore.selectors.list)
  const selectedDate = useAppSelector(riskStore.selectors.selectedDate)
  const [selected, setSelected] = useState<string>("excel")
  const [status, setStatus] = useState(STATUS_LOADED)

  async function confirm() {
    if (selected === "excel") {
      exportRisksToXlsx(risks, establishment, organization, units, _session.accountColors.brandPrimary)
    } else if (selected === "pdf") {
      setStatus(STATUS_SAVING)
      await exportRisksToPdf(dispatch, risks, establishment, organization, units, _session, selectedDate)
      setStatus(STATUS_LOADED)
      onClose()
      return
    } else if (selected === "link") {
      const baseUrl = window.location.origin
      const previewUrl = `${baseUrl}/risks/document/preview?establishmentId=${establishment.id}&organizationId=${organization.id}`

      navigator.clipboard
        .writeText(previewUrl)
        .then(() => {
          toast(t("Lien copié dans le presse-papiers"))
          onClose()
        })
        .catch(() => {
          toast(t("Erreur lors de la copie du lien"), { type: "error" })
        })
      return
    }

    onClose()
  }

  return (
    <Modal
      title="Exporter le document"
      onClose={() => onClose()}
      onNext={() => confirm()}
      status={status}
      isCloseButtonVisible
      disableKeyEvents>
      {risks.length === 0 ? (
        <div className="text-gray-600">{t("Le document ne comporte pas de risques.")}</div>
      ) : (
        <CardContainer>
          {CARDS.map((card) => (
            <div key={card.id}>
              <CardButton
                icon={card.icon}
                iconColor={_session.accountColors.brandPrimary}
                title={card.title}
                isCta={selected === card.id}
                onClick={() => setSelected(card.id)}
              />
              <div className="flex">
                {selected === card.id && (
                  <div
                    className="grey-t flex-auto"
                    style={{ width: 180, height: 74, textAlign: "center", fontSize: 12 }}>
                    {card.text}
                  </div>
                )}
              </div>
            </div>
          ))}
        </CardContainer>
      )}
    </Modal>
  )
}
